import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';

const ROLES = [
  { value: 'admin',    label: 'Admin',     desc: 'Full access — add, edit, delete',   color: '#137333', bg: '#e6f4ea' },
  { value: 'adduser',  label: 'Add User',  desc: 'Can add & edit — no delete',        color: '#b45309', bg: '#fef3c7' },
  { value: 'readonly', label: 'Read Only', desc: 'View only — no add, edit or delete', color: '#1a73e8', bg: '#e8f0fe' },
];

const EMPTY = { name: '', username: '', email: '', phone: '', designation: '', role: 'readonly', isActive: true, notes: '', password: '' };
const PWD_EMPTY = { newPassword: '', confirmPassword: '' };

function roleBadge(r) {
  const found = ROLES.find(x => x.value === r) || ROLES[2];
  return <span style={{ background: found.bg, color: found.color, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{found.label}</span>;
}

export default function AquaUsers() {
  const [users, setUsers]     = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [tab, setTab]         = useState('list');
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [saving, setSaving]   = useState(false);

  // Change-password state
  const [pwdForm, setPwdForm]   = useState(PWD_EMPTY);
  const [pwdErr, setPwdErr]     = useState('');
  const [pwdOk, setPwdOk]       = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const load = () => {
    setLoading(true);
    aquaApi.getUsers().then(rows => setUsers(rows || [])).catch(() => setUsers([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await aquaApi.updateUser(editId, form);
      else await aquaApi.createUser(form);
      setForm(EMPTY); setEditId(null); setTab('list'); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (u) => {
    setForm({
      name: u.name || '', username: u.username || '', email: u.email || '',
      phone: u.phone || '', designation: u.designation || '',
      role: u.role || 'readonly', isActive: u.is_active !== 0, notes: u.notes || '', password: ''
    });
    setEditId(u.id); setTab('form');
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the team?`)) return;
    await aquaApi.deleteUser(id); load();
  };

  const handlePwdChange = async () => {
    setPwdErr(''); setPwdOk('');
    if (!pwdForm.newPassword) { setPwdErr('New password is required'); return; }
    if (pwdForm.newPassword.length < 6) { setPwdErr('Password must be at least 6 characters'); return; }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) { setPwdErr('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      const res = await fetch('/spring/school-api/users/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pwdForm),
      });
      const data = await res.json();
      if (data.success) { setPwdOk('Password changed successfully!'); setPwdForm(PWD_EMPTY); }
      else setPwdErr(data.error || data.message || 'Password change failed');
    } catch (e) { setPwdErr(e.message); }
    finally { setPwdSaving(false); }
  };

  if (loading) return <div className="loading-msg">Loading team…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`}
          onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); }}>
          👥 Team ({users.length})
        </button>
        <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => setTab('form')}>
          {editId ? '✏️ Edit Member' : '➕ Add Member'}
        </button>
        <button className={`tab${tab === 'pwd' ? ' active' : ''}`}
          onClick={() => { setTab('pwd'); setPwdErr(''); setPwdOk(''); setPwdForm(PWD_EMPTY); }}>
          🔑 Change Password
        </button>
      </div>

      {tab === 'list' && (
        <div>
          {/* Role legend */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <div key={r.value} style={{ background: r.bg, border: `1px solid ${r.color}30`, borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
                <span style={{ color: r.color, fontWeight: 700 }}>{r.label}</span>
                <span style={{ color: '#5f6368', marginLeft: 8 }}>{r.desc}</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">👥 Team Members</div>
              <button className="btn btn-primary" style={{ padding: '5px 16px', fontSize: 13 }}
                onClick={() => { setForm(EMPTY); setEditId(null); setTab('form'); }}>
                ➕ Add Member
              </button>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Designation</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                      No team members yet — click <strong>Add Member</strong> to register the first employee
                    </td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.name}</strong></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.username || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.email || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.phone || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.designation || '—'}</td>
                      <td>{roleBadge(u.role)}</td>
                      <td>
                        {u.is_active
                          ? <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>Active</span>
                          : <span style={{ background: '#f1f3f4', color: '#9aa0a6', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>Inactive</span>
                        }
                      </td>
                      <td style={{ fontSize: 12, color: '#888' }}>{u.created_at || '—'}</td>
                      <td>
                        <button className="btn-sm btn-secondary" onClick={() => handleEdit(u)} style={{ marginRight: 4 }}>Edit</button>
                        <button className="btn-sm btn-danger" onClick={() => handleDelete(u.id, u.name)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'pwd' && (
        <div className="card" style={{ maxWidth: 440 }}>
          <div className="card-header"><div className="card-title">🔑 Change Admin Password</div></div>
          <div className="card-body">
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#e8f0fe', borderRadius: 8, fontSize: 13, color: '#1a73e8' }}>
              This changes the password for your main admin (owner) login account.
            </div>
            {pwdErr && <div className="alert alert-error" style={{ marginBottom: 12, padding: '8px 14px', background: '#fce8e6', color: '#c5221f', borderRadius: 6, fontSize: 13 }}>⚠️ {pwdErr}</div>}
            {pwdOk  && <div style={{ marginBottom: 12, padding: '8px 14px', background: '#e6f4ea', color: '#137333', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>✅ {pwdOk}</div>}
            <div className="form-group">
              <label className="form-label">New Password <span style={{ color: '#c5221f' }}>*</span></label>
              <input className="form-control" type="password" value={pwdForm.newPassword}
                onChange={e => setPwdForm(f => ({ ...f, newPassword: e.target.value }))}
                placeholder="Minimum 6 characters"
                style={pwdForm.newPassword && pwdForm.newPassword.length < 6 ? {} : {}} />
              {pwdForm.newPassword && pwdForm.newPassword.length < 6 &&
                <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Minimum 6 characters</div>}
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Confirm New Password <span style={{ color: '#c5221f' }}>*</span></label>
              <input className="form-control" type="password" value={pwdForm.confirmPassword}
                onChange={e => setPwdForm(f => ({ ...f, confirmPassword: e.target.value }))}
                placeholder="Re-enter new password"
                onKeyDown={e => e.key === 'Enter' && handlePwdChange()}
                style={pwdForm.confirmPassword && pwdForm.newPassword !== pwdForm.confirmPassword ? {} : {}} />
              {pwdForm.confirmPassword && pwdForm.newPassword !== pwdForm.confirmPassword &&
                <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Passwords do not match</div>}
            </div>
            <div style={{ marginTop: 18 }}>
              <button className="btn btn-primary" onClick={handlePwdChange} disabled={pwdSaving}>
                {pwdSaving ? 'Updating…' : '🔑 Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'form' && (
        <div className="card" style={{ maxWidth: 680 }}>
          <div className="card-header">
            <div className="card-title">{editId ? '✏️ Edit Team Member' : '➕ Register New Team Member'}</div>
          </div>
          <div className="card-body">
            {err && <div className="alert alert-danger" style={{ marginBottom: 16, padding: '8px 14px', background: '#fce8e6', color: '#c5221f', borderRadius: 6, fontSize: 13 }}>⚠️ {err}</div>}

            <div className="aq-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Ravi Kumar" />
              </div>
              <div className="form-group">
                <label className="form-label">Login Username</label>
                <input className="form-control" value={form.username} onChange={e => set('username', e.target.value)} placeholder="Same username as the login page" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 9XXXXXXXXX" />
              </div>
              <div className="form-group">
                <label className="form-label">Designation</label>
                <input className="form-control" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="e.g. Farm Supervisor" />
              </div>
              <div className="form-group">
                <label className="form-label">Role *</label>
                <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{editId ? 'New Password' : 'Password *'}</label>
                <input className="form-control" type="password" value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={editId ? 'Leave blank to keep current password' : 'Set a login password'} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Used to log in from the <strong>Team Member Login</strong> section on the sign-in page
                </div>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes" />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="isActive" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} style={{ width: 16, height: 16 }} />
                <label htmlFor="isActive" className="form-label" style={{ margin: 0 }}>Active (can log in)</label>
              </div>
            </div>

            {/* Role explanation box */}
            {form.role && (
              <div style={{ marginTop: 16, background: ROLES.find(r => r.value === form.role)?.bg || '#f8f9fa', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                <strong style={{ color: ROLES.find(r => r.value === form.role)?.color }}>
                  {ROLES.find(r => r.value === form.role)?.label} Role:
                </strong>
                <ul style={{ margin: '6px 0 0 0', paddingLeft: 20, color: '#444' }}>
                  {form.role === 'admin' && <>
                    <li>Full access to all data</li>
                    <li>Can add, edit and delete any record</li>
                    <li>Can manage team members</li>
                  </>}
                  {form.role === 'adduser' && <>
                    <li>Can view all data and reports</li>
                    <li>Can add new records and edit existing ones</li>
                    <li>Cannot delete any records</li>
                    <li>Cannot access Team management</li>
                  </>}
                  {form.role === 'readonly' && <>
                    <li>Can view all data and reports</li>
                    <li>Cannot add, edit or delete any records</li>
                    <li>Cannot access Team management</li>
                  </>}
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Register'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); setErr(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
